import { invariant } from '../util/invariant';
import type { Mp4Info } from './mp4Types';
import { getCodecString, getProgressive, type AudioSpecificConfig, type Avc1VisualSampleEntry, type AvcC, type EditListBox, type ElementaryStreamDescriptorBox, type HandlerBox, type MovieHeaderBox, type Mp4aBox, type Mp4Box, type SampleDescriptionBox, type TrackHeaderBox } from './parser';
import { populateVirtualSegments } from './virtualSegments';

export function inspectMoov(
  mp4Info: Mp4Info | undefined,
  moovBox: Mp4Box,
  virtualSegmentUrl: string | undefined,
  ignoreAudio: boolean,
): Mp4Info {
  const videoTracks = mp4Info ? mp4Info.videoTracks : [];
  const audioTracks = mp4Info ? mp4Info.audioTracks : [];
  let virtualSegments = mp4Info ? mp4Info.virtualSegments : null;

  const nExistingAudioTracks = audioTracks.length;

  let videoStblBoxes: Mp4Box[] = [];
  let audioStblBoxes: Mp4Box[] = [];

  moovBox.children.forEach(box => {
    if (box.type === 'trak') {
      const tkhdBox = box.children.find(b => b.type === 'tkhd') as TrackHeaderBox | undefined;
      invariant(tkhdBox, 'Could not find tkhd box');
      const trackId = tkhdBox.track_id;

      let mediaStartPtsOffset = 0;
      const edtsBox = box.children.find(b => b.type === 'edts') as Mp4Box | undefined;
      if (edtsBox !== undefined) {
        const elstBox = edtsBox.children.find(b => b.type === 'elst') as EditListBox | undefined;
        if (elstBox !== undefined) {
          if (elstBox.entries.length > 0) {
            mediaStartPtsOffset = elstBox.entries[0].media_time;
          }
        }
      }

      const mdiaBox = box.children.find(b => b.type === 'mdia');
      invariant(mdiaBox, 'Could not find mdia box');

      const mdhdBox = mdiaBox.children.find(b => b.type === 'mdhd') as MovieHeaderBox | undefined;
      invariant(mdhdBox, 'Could not find mdhd box');
      const timescale = mdhdBox.timescale;

      const hdlrBox = mdiaBox.children.find(b => b.type === 'hdlr') as HandlerBox | undefined;
      invariant(hdlrBox, 'Could not find hdlr box');

      const minfBox = mdiaBox.children.find(b => b.type === 'minf') as Mp4Box | undefined;
      invariant(minfBox, 'Could not find minf box');

      const stblBox = minfBox.children.find(b => b.type === 'stbl') as Mp4Box | undefined;
      invariant(stblBox, 'Could not find stbl box');

      const stsdBox = stblBox.children.find(b => b.type === 'stsd') as SampleDescriptionBox | undefined;
      invariant(stsdBox, 'Could not find stsd box');

      if (hdlrBox.handler_type === 'vide') {
        // Video
        const avc1Box = stsdBox.children.find(b => b.type === 'avc1') as Avc1VisualSampleEntry | undefined;
        invariant(avc1Box, 'Could not find avc1 box');

        const avcCBox = avc1Box.children.find(b => b.type === 'avcC') as AvcC | undefined;
        invariant(avcCBox, 'Could not find avcC box');
        const avcC = new Uint8Array(avcCBox.avcC);
        const width = avc1Box.width;
        const height = avc1Box.height;

        videoTracks.push({
          codecString: getCodecString(avcCBox),
          progressive: getProgressive(avcCBox),
          trackId,
          timescale,
          avcC,
          width,
          height,
          frameDuration: 0,
          mediaStartPtsOffset,
        });
        videoStblBoxes.push(stblBox);
      } else if (hdlrBox.handler_type  === 'soun' && !ignoreAudio) {
        const mp4aBox = stsdBox.children.find(b => b.type === 'mp4a') as Mp4aBox | undefined;
        invariant(mp4aBox, 'Could not find mp4a box');

        const sampleRate = mp4aBox.samplerate;
        const bitsPerSample = mp4aBox.samplesize;

        // We have observed that the channel count in the mp4a box is sometimes
        // incorrect. To avoid issues when this is the case, we try to extract
        // the codec specific information from the esds box
        let channelCount = mp4aBox.channelcount;
        const esdsBox = mp4aBox.children.find(b => b.type === 'esds') as ElementaryStreamDescriptorBox | undefined;
        if (esdsBox) {
          const audioSpecificConfig = esdsBox.es.decConfigDescr.decSpecificInfo.find(info => typeof (info as any).audioObjectType === 'number') as AudioSpecificConfig | undefined;
          if (audioSpecificConfig) {
            switch(audioSpecificConfig.channelConfiguration) {
              case 1:
              case 2:
              case 3:
              case 4:
              case 5:
              case 6: channelCount = audioSpecificConfig.channelConfiguration; break;
              case 7: channelCount = 8; break;
              default: break;
            }
          }
        }

        // Audio
        audioTracks.push({
          trackId,
          timescale,
          sampleRate,
          bitsPerSample,
          channelCount,
          frameDuration: 0,
          mediaStartPtsOffset,
        });
        audioStblBoxes.push(stblBox);
      }
    }
  });

  if (virtualSegmentUrl !== undefined) {
    // If we have video, we want to create the virtual segments after the GOPs
    invariant(videoTracks.length <= 1, 'Cannot handle more than 1 video stream');
    invariant(videoStblBoxes.length <= 1, 'Cannot handle more than 1 video stream inside file');
    if (videoStblBoxes.length === 1) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, null, videoTracks[0].mediaStartPtsOffset, videoTracks[0].timescale, videoStblBoxes[0]);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0].videoData!.samples.length > 0) {
        videoTracks[0].frameDuration = virtualSegments[0].videoData!.samples[0].duration;
      }
    }

    for (let i = nExistingAudioTracks; i < audioTracks.length; i++) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, i, audioTracks[i].mediaStartPtsOffset, audioTracks[i].timescale, audioStblBoxes[i - nExistingAudioTracks]);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0].audioData[i].samples.length > 0) {
        audioTracks[i].frameDuration = virtualSegments[0].audioData[i].samples[0].duration;
      }
    }
  }

  return {
    videoTracks,
    audioTracks,
    virtualSegments: virtualSegments ?? [],
  };
}
